// ===========================================
// Invoice Detail Dialog
// ===========================================
// Detailed view of an invoice for parents

import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Chip,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { Invoice } from '../../api/invoices.api';
import { formatCurrencyFromDollars, paymentDetailText } from '../../utils/currency';
import type { ParentFamilyCredit } from '../../api/invoices.api';
import CreditOnAccountNotice from './CreditOnAccountNotice';
import {
  INVOICE_STATUS_COLORS as STATUS_COLORS,
  PAYMENT_METHOD_LABELS,
  invoiceStatusLabel,
  invoiceBalance,
  formatPaymentDay,
} from '../../utils/invoiceDisplay';
import { formatUTCDate } from '../../utils/dateUtils';
import { displayLineDescription } from '../../utils/invoiceLineDescription';
import { appliedCoverageSuffix } from '../../utils/discountCoverage';
import { useSchoolTimezone } from '../../contexts/TenantContext';
import InvoiceAmendedNote from './InvoiceAmendedNote';
import FamilyLateFeeLines from './FamilyLateFeeLines';

// ===========================================
// HELPER FUNCTIONS
// ===========================================

// Invoice money is stored in dollars.
const formatCurrency = formatCurrencyFromDollars;

function formatDate(dateString: string): string {
  return new Intl.DateTimeFormat('en-AU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(dateString));
}

// ===========================================
// COMPONENT PROPS
// ===========================================

interface InvoiceDetailDialogProps {
  /** #642 Phase 2: the family's own credit, from the page's `useParentFamilyCredit`. */
  familyCredit?: ParentFamilyCredit;
  open: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  onDownload?: (invoice: Invoice) => void;
  isDownloadLoading?: boolean;
}

// ===========================================
// COMPONENT
// ===========================================

export default function InvoiceDetailDialog({
  open,
  onClose,
  invoice,
  onDownload,
  isDownloadLoading = false,
  familyCredit,
}: InvoiceDetailDialogProps) {
  // Before the early return: hooks must run on every render
  const timeZone = useSchoolTimezone();
  if (!invoice) return null;

  const balance = invoiceBalance(invoice);
  const isOverdue =
    invoice.status !== 'PAID' &&
    invoice.status !== 'CANCELLED' &&
    new Date(invoice.dueDate) < new Date();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" component="span">
            Invoice {invoice.invoiceNumber}
          </Typography>
          <Chip
            label={invoiceStatusLabel(invoice.status)}
            size="small"
            sx={{
              ml: 2,
              backgroundColor: STATUS_COLORS[invoice.status].bg,
              color: STATUS_COLORS[invoice.status].text,
              fontWeight: 600,
            }}
          />
          <InvoiceAmendedNote latestAmendment={invoice.latestAmendment} timeZone={timeZone} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onDownload && (
            <IconButton
              onClick={() => onDownload(invoice)}
              disabled={isDownloadLoading}
              size="small"
              title="Download PDF"
            >
              <DownloadIcon />
            </IconButton>
          )}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Invoice Info */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              Invoice Number
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {invoice.invoiceNumber}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              Due Date
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: isOverdue ? 'error.main' : 'text.primary',
              }}
            >
              {formatUTCDate(invoice.dueDate, { day: '2-digit', month: '2-digit', year: 'numeric' })}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              Created
            </Typography>
            <Typography variant="body1">
              {formatDate(invoice.createdAt)}
            </Typography>
          </Grid>
          {invoice.sentAt && (
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">
                Sent
              </Typography>
              <Typography variant="body1">
                {formatDate(invoice.sentAt)}
              </Typography>
            </Grid>
          )}
        </Grid>

        {invoice.description && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" color="text.secondary">
              Description
            </Typography>
            <Typography variant="body1">{invoice.description}</Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Line Items Table */}
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          Line Items
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#FCF6E6' }}>
                <TableCell>Description</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Unit Price</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoice.InvoiceItem.map((item) => (
                <TableRow key={item.id}>
                  <TableCell sx={{ whiteSpace: 'pre-line' }}>{displayLineDescription(item.description)}</TableCell>
                  <TableCell align="right">{item.quantity}</TableCell>
                  <TableCell align="right">
                    {formatCurrency(item.unitPrice)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 500 }}>
                    {formatCurrency(item.total)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Discounts Section */}
        {invoice.InvoiceDiscount && invoice.InvoiceDiscount.length > 0 && (
          <Box sx={{ mt: 3, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              Discounts Applied
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#96DAC9' }}>
                    <TableCell>Discount</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.InvoiceDiscount.map((discount) => (
                    <TableRow key={discount.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {discount.name}
                          {/* #625 D9: what it covers, as the PDF says it. */}
                          {appliedCoverageSuffix(discount)}
                        </Typography>
                        {discount.description && (
                          <Typography variant="caption" color="text.secondary">
                            {discount.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500, color: 'warning.main' }}>
                        -{formatCurrency(discount.calculatedAmount)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Total Discounts
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'warning.main' }}>
                      -{formatCurrency(
                        invoice.InvoiceDiscount.reduce(
                          (sum, discount) => sum + parseFloat(discount.calculatedAmount),
                          0
                        )
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* #372: each fee as its own list, OUTSIDE the summary column, whose "Late fees" row is
            their total — inside it the same $25 read twice above Total. A waived fee is struck
            through (never a reason). */}
        {(invoice.InvoiceLateFee ?? []).length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              Late fees
            </Typography>
            <FamilyLateFeeLines fees={invoice.InvoiceLateFee} timeZone={timeZone} />
          </Box>
        )}

        {/* Payment Summary */}
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          Payment Summary
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography color="text.secondary">Subtotal</Typography>
              <Typography>{formatCurrency(invoice.subtotal)}</Typography>
            </Box>
            {invoice.InvoiceDiscount && invoice.InvoiceDiscount.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="warning.main">Discounts</Typography>
                <Typography color="warning.main" sx={{ fontWeight: 500 }}>
                  -{formatCurrency(
                    invoice.InvoiceDiscount.reduce(
                      (sum, discount) => sum + parseFloat(discount.calculatedAmount),
                      0
                    )
                  )}
                </Typography>
              </Box>
            )}
            {/* #372: the server's late-fee rows (a taxable fee before Tax, a plain one after). */}
            {(invoice.lateFeeRows ?? []).filter((row) => row.beforeTax).map((row) => (
              <Box key={`b-${row.label}`} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">{row.label}</Typography>
                <Typography>{formatCurrency(row.amount)}</Typography>
              </Box>
            ))}
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography color="text.secondary">Tax</Typography>
              <Typography>{formatCurrency(invoice.tax)}</Typography>
            </Box>
            {(invoice.lateFeeRows ?? []).filter((row) => !row.beforeTax).map((row) => (
              <Box key={`a-${row.label}`} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">{row.label}</Typography>
                <Typography>{formatCurrency(row.amount)}</Typography>
              </Box>
            ))}
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ fontWeight: 600 }}>Total</Typography>
              <Typography sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
                {formatCurrency(invoice.total)}
              </Typography>
            </Box>
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography color="success.main">Amount Paid</Typography>
              <Typography color="success.main" sx={{ fontWeight: 500 }}>
                {formatCurrency(invoice.amountPaid)}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                p: 1.5,
                bgcolor: balance.owing ? 'warning.light' : invoice.status === 'CANCELLED' ? 'grey.200' : 'success.light',
                borderRadius: 1,
                mt: 1,
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>{balance.label}</Typography>
              <Typography sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
                {formatCurrency(balance.amount)}
              </Typography>
            </Box>
            {/* #642 Phase 2: the family's credit on a live invoice (none on a VOID one). */}
            {invoice.status !== 'CANCELLED' && (
              <Box sx={{ mt: 1 }}>
                <CreditOnAccountNotice credit={familyCredit} />
              </Box>
            )}
          </Box>
        </Paper>

        {/* Payment History */}
        {invoice.Payment && invoice.Payment.length > 0 && (
          <>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              Payment History
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {invoice.Payment.map((payment) => (
                <Paper
                  key={payment.id}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    bgcolor: '#FCF6E6',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {formatCurrency(payment.amount)}
                      </Typography>
                      {paymentDetailText(payment.processingFee, Number(payment.toCredit ?? 0)) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {paymentDetailText(payment.processingFee, Number(payment.toCredit ?? 0))}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {PAYMENT_METHOD_LABELS[payment.method]}
                        {payment.reference && ` - Ref: ${payment.reference}`}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {formatPaymentDay(payment.paidAt, timeZone)}
                    </Typography>
                  </Box>
                  {payment.notes && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}
                    >
                      {payment.notes}
                    </Typography>
                  )}
                </Paper>
              ))}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
